import type { MeFieldsFragment } from '@mbfab/client-api'
import { api } from '@mbfab/client-api'
import { useRedirect } from '@mbfab/frontend-common'
import { isSome, VERSION } from '@mbfab/shared'
import { Button, Layout, Result } from 'antd'
import dayjs from 'dayjs'
import LogRocket from 'logrocket'
import type { AppProps } from 'next/app'
import platform from 'platform'
import { Suspense, useEffect, useRef } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import { ErrorPage } from '../components/ui/ErrorPage'
import { SuspenseWithLoading } from '../components/ui/SuspenseWithLoading'
import { routes } from '../config/routes'
import { AuthenticatedApolloProvider } from '../providers/AuthenticatedApolloProvider'
import { setUserInfoToLocalStorage } from '../services/getUserInfoFromLocalStorage'
import { logger } from '../services/logger'
import { LOGGER_EVENTS } from '../services/logger_events'
import { Posthog } from '../services/posthog'
import { getTenantIdFromPath } from '../util'

import { DefaultHead } from './base/DefaultHead'
import { Sider } from './member/Sider'

const isOnboardingPage = (pathname: string, tenantId: string) => {
  return [
    routes.termsOfUsePath(tenantId),
    routes.planConfirmPath(tenantId),
    routes.planCompletePath(tenantId),
  ].includes(pathname)
}

export const MemberLayout = (props: AppProps) => {
  return (
    <Suspense>
      <AuthenticatedApolloProvider>
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <SuspenseWithLoading>
            <>
              <Suspense>
                <LogicBodyWrapper {...props} />
              </Suspense>
            </>
          </SuspenseWithLoading>
        </ErrorBoundary>
      </AuthenticatedApolloProvider>
    </Suspense>
  )
}

function LogicBodyWrapper(appProps: AppProps) {
  const meQuery = api.useFetchMeQuery()
  if (isSome(meQuery.error)) {
    return <ErrorPage error={meQuery.error} />
  }
  const me = meQuery.data?.me
  if (me === undefined) {
    return <></>
  }

  return <LogicBody {...appProps} me={me} />
}

function LogicBody({ Component, router, me }: AppProps & { me: MeFieldsFragment }) {
  const redirect = useRedirect()

  const posthogProperty = {
    name: `${me.name} / ${me.tenant.name}`,
    email: me.email || 'unknown@buffup.jp',
    // description: me.team?.name ?? '',
    tenantId: me.tenant.id || 'Unknown',
    tenantName: me.tenant.name || 'Unknown',
    userId: me.id || 'Unknown',
    userName: me.name || 'Unknown',
    role: me.role || 'Unknown',
    // signUpDate: me.createdAt,
    // posthog.ts側で不要なidentityやpeople.setが走ってトラッキング回数が増えるのを防ぐため、キャッシュの機構をいれているが、
    // posthogの挙動が怪しく、property全てが記録されないような挙動を見せている。
    // 何度かやり直すとこれが防げるようなので、とりあえず日毎に1回やり直すため、以下のpropertyを設定しておく
    propertyConfiguredAt: dayjs().format(`YYYY-MM-DD`),
    platform: platform.toString(),
  }
  const posthogProperties = [
    { name: posthogProperty.name },
    { email: posthogProperty.email },
    // { role: posthogProperty.role },
    // { description: posthogproperty.meta.label },
    { tenantId: posthogProperty.tenantId },
    { tenantName: posthogProperty.tenantName },
    { userId: posthogProperty.userId },
    { userName: posthogProperty.userName },
    { role: posthogProperty.role },
    // { signUpDate: posthogProperty.signUpDate },
    { propertyConfiguredAt: posthogProperty.propertyConfiguredAt },
    { platform: posthogProperty.platform },
  ]

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    console.log(`mbfabへようこそ! お使いのバージョンは${VERSION}です`) // eslint-disable-line no-console
    void logger.info(LOGGER_EVENTS.VISIT, ``, posthogProperty)
  }, [])

  useEffect(() => {
    setUserInfoToLocalStorage(me)

    Posthog.identifyOnce(me.id, posthogProperties)
    Posthog.setPeopleOnce(me.id, posthogProperties) // identifyOnceでもpropertyの設定はできるが、一度設定した後に上書きができないので、プロパティが変わった際はここで上書きする
    LogRocket.identify(me.id, {
      name: me.name,
      email: me.email,
      tenantName: me.tenant.name,
      role: me.role,
    })
  }, [])

  useEffect(() => {
    Posthog.trackAccess(router.pathname, posthogProperty)
  }, [router.pathname])

  // 現状だと、ログインロジックを通常メンバー側と使いまわしているため、通常メンバーでもここまでは到達できる
  // 実際は、各種のエンドポイントにアクセスできないため結局エラーになるが、
  // ここでroleを確認して出しわけしておく

  if (me.role !== 'manager' && me.role !== 'admin') {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <Result
          status="error"
          title="権限がありません"
          subTitle="このページにアクセスする権限がありません。別のアカウントでログインし直してください。"
          extra={
            <Button
              type="link"
              onClick={() => {
                redirect(routes.signOutPath())
              }}
            >
              ログアウト
            </Button>
          }
        />
      </div>
    )
  }

  if (me.tenant.status === 'preActive' && !isOnboardingPage(router.asPath, me.tenant.id)) {
    // リダイレクトする
    redirect(routes.termsOfUsePath(getTenantIdFromPath()))
    return <></>
  }

  return (
    <>
      <DefaultHead tenantName={me.tenant.name} />
      <Suspense fallback={<div>loaidng</div>}>
        <LayoutBody Component={Component} router={router} />
      </Suspense>
    </>
  )
}

function LayoutBody({ Component, router }: Pick<AppProps, 'Component' | 'router'>) {
  const asPath = router.asPath
  const nodeRef = useRef<HTMLDivElement>(null)

  return (
    <Suspense>
      <Layout style={{ height: '100%' }} className="w-full">
        {isOnboardingPage(asPath, getTenantIdFromPath()) ? <></> : <Sider />}
        {/* {isMobile && <MobileHeader me={me}></MobileHeader> } TODO */}
        {/* インターコム分の余白をとるなら、pb-16が必要 */}
        <Layout.Content className={`overflow-visible-print h-full overflow-y-auto pb-16`}>
          <SwitchTransition>
            <CSSTransition key={router.asPath} timeout={200} classNames="page-transition" nodeRef={nodeRef}>
              <div ref={nodeRef}>
                <ErrorBoundary FallbackComponent={ErrorPage}>
                  <SuspenseWithLoading>
                    <Component />
                  </SuspenseWithLoading>
                </ErrorBoundary>
              </div>
            </CSSTransition>
          </SwitchTransition>
        </Layout.Content>
      </Layout>
    </Suspense>
  )
}
